import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { jwtDecode } from 'jwt-decode';
import { Eye, EyeOff, Fingerprint, Loader2, ScanFace } from 'lucide-react';
import _ from 'lodash';

import { analytics } from '../services/analytics';

import ProjectConfig from '../constants';
import login from '../api/login/login';
import { isValidLoginIdentifier, normalizeLoginIdentifier } from '../utils/loginIdentifier';
import setUserPassword from '../api/users/setUserPassword';
import getMaintenancePublic from '../api/generic-api/getMaintenancePublic';
import { ArcaContext } from '../context/ArcaContext.context';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { JWTDecodedToken } from '../types/auth';
import { esTokenDeImpersonacion } from '../utils/impersonation';
import { markTokenLoginIntent } from '../utils/tokenLoginIntent';
import { recordarAccesoTrasLogin } from '../movil/accesoTrasLogin';
import { ResultadoDeEntrar, useEntrarConBiometria } from '../movil/useEntrarConBiometria';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import CreatePasswordModalV2 from './Modals/CreatePasswordModalV2';
import SecurityWarningModalV2 from './Modals/SecurityWarningModalV2';
import { toast } from './ui/sonner';
import { conTipo } from '../movil/textosBiometria';
import resolveRealCompanyId from '../utils/resolveRealCompanyId';

// TODO: Crear GenericModalV2 con shadcn Dialog cuando se migre el primer flujo que lo necesite
// (invitaciones, onboarding, etc.). Por ahora Login usa toast para errores.

/** Lo que responde comparsa (`AuthService.login`) a un NIF o una contraseña que no valen. */
const CREDENCIALES_INCORRECTAS = 'USER_NOT_FOUND_OR_WRONG_PASSWORD';

/**
 * Lo más que se espera a que el login por enlace acabe antes de devolver el formulario. Va por
 * encima de los 10 s de `getMaintenancePublic`, que es la única espera de red del camino.
 */
const TOPE_LOGIN_POR_ENLACE_MS = 12_000;

const LoginFormV2 = ({ token }: { token?: string }) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	// Un único campo: el NIF es el identificador, pero se sigue admitiendo el correo
	// para quien todavía no lo tenga registrado. `login` decide cuál manda por la "@".
	const [identifier, setIdentifier] = useState('');
	const [nifError, setNifError] = useState<'formato' | 'correo' | null>(null);
	const [password, setPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	// Sesión ya iniciada que aún tiene que establecer contraseña. Se guarda para poder
	// llamar a `setUserPassword` sin volver a leer el token.
	const [pendingSession, setPendingSession] = useState<{
		token: string;
		userId: string;
		persistSession: () => void;
	} | null>(null);
	const [passwordModal, setPasswordModal] = useState<'create' | 'update' | null>(null);
	// Con token —enlaces de los correos, recuperación, impersonación de cuarteto, app móvil y el
	// «Ver planes mejores» del funnel B2C— no se pinta el formulario mientras se valida: asomaba
	// unos milisegundos antes de entrar. Vuelve si el token falla o si se pasa el tope; si vale, la
	// navegación desmonta esta pantalla con el cargador puesto.
	const [validandoToken, setValidandoToken] = useState(Boolean(token));
	/** Ya se devolvió el formulario por el tope: un fallo que llegue después no avisa otra vez. */
	const topeVencido = useRef(false);

	const { setToken, setCompanyId, setUserId, setUserAccountType, setLanguage, setUserScope } =
		useContext(ArcaContext);

	const navigate = useNavigate();
	/** Se pone a `true` solo cuando el API confirma que la contraseña quedó guardada. */
	const passwordEstablished = useRef(false);

	useEffect(() => {
		const processTokenLogin = async (tokenToLogin: string) => {
			setIsLoading(true);
			try {
				const decodedToken = jwtDecode<JWTDecodedToken>(tokenToLogin);
				const canBypassMaintenance = esTokenDeImpersonacion(tokenToLogin);

				if (!canBypassMaintenance) {
					const maintenanceResponse = await getMaintenancePublic();
					const isMaintenanceActive =
						maintenanceResponse.data?.value === 'true' || maintenanceResponse.data?.value === true;

					if (isMaintenanceActive) {
						navigate('/maintenance');
						setIsLoading(false);
						return;
					}
				}

				// 🔴 La sesión NO se persiste todavía cuando hay que establecer contraseña.
				// Si se guardara aquí, bastaría con abrir /dashboard/home en otra pestaña
				// —o desde un marcador, o al día siguiente— para entrar sin haberla puesto,
				// y el modal sería puramente decorativo. Se guarda en `persistSession`, que
				// solo se llama cuando el flujo ha terminado de verdad.
				const persistSession = () => {
					localStorage.setItem('token', tokenToLogin);
					setToken(tokenToLogin);
					// Solo el login por enlace llega aquí: el Dashboard le abrirá su plan
					// individual en proceso si lo tiene. El de contraseña no deja marca.
					markTokenLoginIntent();

					const { id, accountType, companyId, scope, language } = decodedToken;
					const userLanguage = language || 'es';

					// 🔴 Sin empresa, ni `"null"` ni nada: la clave no existe (product#85).
					const realCompanyId = resolveRealCompanyId(companyId);
					setCompanyId(realCompanyId ?? '');
					if (realCompanyId) localStorage.setItem('companyId', realCompanyId);
					else localStorage.removeItem('companyId');

					setUserId(id);
					localStorage.setItem('userId', id);

					setUserAccountType(accountType);
					localStorage.setItem('userAccountType', accountType);

					setLanguage(userLanguage);
					localStorage.setItem('language', userLanguage);

					if (scope) {
						setUserScope(scope);
						localStorage.setItem('userScope', JSON.stringify(scope));
					} else {
						setUserScope(null);
						localStorage.removeItem('userScope');
					}
				};

				const companyName = decodedToken.scope?.companyNames?.[decodedToken.companyId];

				// `canBypassMaintenance` solo lo lleva el token de impersonación de cuarteto
				// (`/hack-token` en comparsa): el empleado no se identifica como el cliente.
				if (canBypassMaintenance) {
					analytics.startImpersonation();
				} else {
					analytics.identify({
						id: decodedToken.id,
						companyId: decodedToken.companyId,
						accountType: decodedToken.accountType,
						email: decodedToken.email,
						companyName,
						language: decodedToken.language || 'es'
					});
					analytics.setUserProperties({
						email: decodedToken.email,
						name: decodedToken.name,
						accountType: decodedToken.accountType,
						companyName,
						language: decodedToken.language || 'es'
					});
					analytics.userLoggedIn({
						accountType: decodedToken.accountType,
						companyId: decodedToken.companyId
					});
				}

				// Quien llega por un enlace de recuperación tiene que establecer contraseña
				// antes de seguir. Lo decide el backend con `mustSetPassword`, no la URL: el
				// enlace de recuperación y el de marketing llevan el MISMO token opaco, así
				// que la única capa capaz de distinguirlos es el JWT que se acuña al canjear.
				// `hasPassword` solo elige el copy.
				//
				// 🔴 Se salta a quien NO tiene NIF. Lo fija la Epic: «Sin NIF (pre-KYC B2C o
				// B2B sin aceptar plan): solo magic link […] No se le pide ni crea contraseña
				// hasta tener NIF». Esa gente entra siempre por enlace y su NIF llegará por el
				// KYC; pedirles contraseña es pedirles algo que su flujo no usa.
				//
				// Se compara con `=== false` a propósito: si el backend no manda el campo se
				// asume que sí tiene y se pide igual, que es el lado seguro.
				if (decodedToken.mustSetPassword && decodedToken.hasNif !== false) {
					setPendingSession({ token: tokenToLogin, userId: decodedToken.id, persistSession });
					setPasswordModal(decodedToken.hasPassword === false ? 'create' : 'update');
					setIsLoading(false);
					return;
				}

				persistSession();
				navigate('/dashboard/home');
			} catch (error) {
				console.error('Error during token login:', error);
				if (!topeVencido.current) {
					toast.error(translate('general.login.errorMessage'));
				}
				setIsLoading(false);
				setValidandoToken(false);
			}
		};

		if (!token) return;

		// 🔴 Si la validación se cuelga, no se deja a nadie mirando el cargador: pasado el tope
		// vuelve el formulario con un aviso. Si el token resulta bueno después, se entra igual —
		// quien llega sin NIF no tiene contraseña, y el formulario no le serviría—; si falla
		// después, no se repite el aviso.
		const tope = setTimeout(() => {
			topeVencido.current = true;
			toast.error(translate('general.login.tokenLoginTimeout'));
			setValidandoToken(false);
		}, TOPE_LOGIN_POR_ENLACE_MS);

		processTokenLogin(token).finally(() => clearTimeout(tope));

		return () => clearTimeout(tope);
	}, [
		token,
		navigate,
		translate,
		setToken,
		setCompanyId,
		setUserId,
		setUserAccountType,
		setLanguage,
		setUserScope
	]);

	/**
	 * Entra con un NIF y una contraseña: los que escribe el usuario o los que guardó Face ID
	 * (`conAccesoGuardado`). Responde cómo fue, para que Face ID sepa si lo guardado sigue valiendo:
	 * `credenciales` lo invalida; `otro` (red, mantenimiento) no.
	 */
	const entrar = async (
		nif: string,
		contrasena: string,
		conAccesoGuardado = false
	): Promise<ResultadoDeEntrar> => {
		setIsLoading(true);

		// 🔴 Se comprueba aquí y no solo en el backend porque el backend responde igual
		// tanto si el NIF no existe como si la contraseña falla —no delata cuentas—. Sin
		// este aviso, quien teclea mal un dígito lee «credenciales incorrectas» y se pone a
		// probar contraseñas sin saber que el fallo está en la otra línea.
		if (!isValidLoginIdentifier(nif)) {
			// 🔴 A quien escribe su correo no se le puede decir «revisa tu NIF»: no tiene
			// ninguno que revisar, así que el consejo es irrealizable y le deja reintentando.
			// Necesita saber por dónde SÍ puede entrar.
			setNifError(nif.includes('@') ? 'correo' : 'formato');
			setIsLoading(false);
			return 'credenciales';
		}

		try {
			const maintenanceResponse = await getMaintenancePublic();
			const isMaintenanceActive =
				maintenanceResponse.data?.value === 'true' || maintenanceResponse.data?.value === true;

			if (isMaintenanceActive) {
				navigate('/maintenance');
				setIsLoading(false);
				return 'otro';
			}

			// Se manda LIMPIO, igual que se validó: el backend no quita separadores.
			const nifLimpio = normalizeLoginIdentifier(nif);
			const response = await login(nifLimpio, contrasena);
			const responseData = response.data;

			if (!response.success || !_.isNil(responseData.error)) {
				toast.error(translate('general.login.errorMessage'));
				setIsLoading(false);
				// Solo la contraseña mala invalida el acceso guardado de Face ID. comparsa responde
				// `success:false` también a fallos pasajeros (error interno, límite de intentos).
				return response.message === CREDENCIALES_INCORRECTAS ? 'credenciales' : 'otro';
			}

			// App móvil: lo recoge «¿Entrar con Face ID la próxima vez?». En la web no hace nada.
			if (!conAccesoGuardado) {
				recordarAccesoTrasLogin({ nif: nifLimpio, contrasena });
			}
			handleLoginSuccess(responseData);
			setUserId(responseData.user.id);
			navigate('/dashboard/home');
			return 'ok';
		} catch (error) {
			console.error('Error al iniciar sesión:', error);
			toast.error(translate('general.login.errorMessage'));
			setIsLoading(false);
			return 'otro';
		}
	};

	const handleLogin = async (event: React.FormEvent) => {
		event.preventDefault();
		await entrar(identifier, password);
	};

	// App móvil: con «Entrar con Face ID» activado, pide la cara al llegar, rellena y entra, y ofrece
	// un botón para hacerlo cuando quiera (tras cerrar sesión no se pide sola). No en el login por
	// enlace, que ya trae su sesión. En la web no hace nada.
	const { tipo: biometria, entrarConBiometria } = useEntrarConBiometria(
		!token,
		({ nif, contrasena }) => {
			setIdentifier(nif);
			setPassword(contrasena);
			return entrar(nif, contrasena, true);
		}
	);

	const handleSubmitNewPassword = async (newPassword: string) => {
		// `throw` y no `return`: una promesa que resuelve sin haber llamado al API haría
		// que el modal cantase «contraseña creada» con la contraseña vieja intacta. Es el
		// mismo patrón que ya hubo que arreglar dentro de `setUserPassword`.
		if (!pendingSession) throw new Error('No hay sesión pendiente que completar');

		await setUserPassword(pendingSession.token, pendingSession.userId, newPassword);
		// El éxito queda registrado como un HECHO, no se deduce de que el modal se cierre.
		passwordEstablished.current = true;
	};

	/**
	 * 🔴 La sesión solo se concede si la contraseña se estableció DE VERDAD.
	 *
	 * Antes bastaba con que el modal se cerrase, y este mismo callback va cableado a la
	 * Root de Radix: cualquier vía de cierre —un `DialogClose` en el cuerpo, un cambio de
	 * versión, la X si algún día se destapa— concedía la sesión con la contraseña sin
	 * establecer. La obligatoriedad se apoyaba en una propiedad del componente hijo en vez
	 * de en un hecho del padre.
	 */
	const handlePasswordModalDone = (open: boolean) => {
		if (open || !pendingSession || !passwordEstablished.current) return;

		pendingSession.persistSession();
		setPasswordModal(null);
		setPendingSession(null);
		navigate('/dashboard/home');
	};

	const handleLoginSuccess = (responseData: any) => {
		const { user, token } = responseData;
		if (user) {
			const { companyId, id: userId, accountType, language } = user;

			// 🔴 `setItem('companyId', null)` guarda la CADENA `"null"`, truthy al recargar: el
			// backend la tomaba por una empresa ajena y apagaba todas las flags (product#85).
			const realCompanyId = resolveRealCompanyId(companyId);
			setCompanyId(realCompanyId ?? '');
			if (realCompanyId) localStorage.setItem('companyId', realCompanyId);
			else localStorage.removeItem('companyId');

			setUserId(userId);
			localStorage.setItem('userId', userId);

			setUserAccountType(accountType);
			localStorage.setItem('userAccountType', accountType);

			setLanguage(language);
			localStorage.setItem('language', language);

			// El token incluye scope.companyNames para usuarios multi-company.
			// Lo decodificamos aquí (mismo patrón que el path de token-decode)
			// para que identify/setUserProperties reciban company_name cuando
			// está disponible, sin fetches extra.
			let companyName: string | undefined;
			if (token) {
				try {
					const decoded = jwtDecode<JWTDecodedToken>(token);
					companyName = decoded.scope?.companyNames?.[companyId];
				} catch (e) {
					console.error('Error decoding token for analytics:', e);
				}
			}

			analytics.identify({
				id: userId,
				companyId,
				accountType,
				email: user.email,
				companyName,
				language: language || 'es'
			});
			analytics.setUserProperties({
				email: user.email,
				name: user.name,
				accountType,
				companyName,
				language: language || 'es'
			});
			analytics.userLoggedIn({
				accountType,
				companyId
			});
		}

		if (token) {
			localStorage.setItem('token', token);
			setToken(token);

			try {
				const decodedToken = jwtDecode<JWTDecodedToken>(token);

				if (decodedToken?.scope) {
					setUserScope(decodedToken.scope);
					localStorage.setItem('userScope', JSON.stringify(decodedToken.scope));
				} else {
					setUserScope(null);
					localStorage.removeItem('userScope');
				}
			} catch (error) {
				console.error('Error decoding token for scope:', error);
				setUserScope(null);
				localStorage.removeItem('userScope');
			}
		}
	};

	// Los dos modales son obligatorios: no tienen "ahora no", ni la X, ni se cierran con
	// Escape ni pinchando fuera. `onOpenChange(false)` solo llega tras un envío correcto.
	if (passwordModal === 'create') {
		return (
			<CreatePasswordModalV2
				open
				onOpenChange={handlePasswordModalDone}
				onCreatePassword={handleSubmitNewPassword}
			/>
		);
	}

	if (passwordModal === 'update') {
		return (
			<SecurityWarningModalV2
				open
				onOpenChange={handlePasswordModalDone}
				onChangePassword={handleSubmitNewPassword}
			/>
		);
	}

	// Va detrás de los modales: con `mustSetPassword` manda el modal, no el cargador.
	if (validandoToken) {
		return (
			<div
				aria-busy="true"
				className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background p-4 text-center"
			>
				<img src="/imgs/LOGO_VERDE_2.svg" alt="" className="h-12" />
				<Loader2
					className="w-12 h-12 text-primary animate-spin motion-reduce:animate-none"
					aria-hidden="true"
				/>
				<p role="status" className="text-muted-foreground">
					{translate('general.login.signingIn')}
				</p>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
			<Card className="w-full max-w-md shadow-lg">
				<CardHeader className="text-center space-y-4">
					<div className="flex justify-center">
						<img src="/imgs/LOGO_VERDE_2.svg" alt="Logo Arca" className="h-12" />
					</div>
					<CardTitle className="text-2xl font-semibold text-gray-900">
						{translate('general.login.title') || 'Iniciar sesión'}
					</CardTitle>
					<CardDescription className="text-gray-600">
						{translate('general.login.subtitle') || 'Accede a tu cuenta de Arca Digital'}
					</CardDescription>
				</CardHeader>

				<form onSubmit={handleLogin}>
					<CardContent className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="identifier" className="text-sm font-medium text-gray-700">
								{translate('general.login.nif')}
							</Label>
							{/*
							 * `type="text"` a propósito: con `type="email"` el navegador bloquearía
							 * el envío de un NIF por no llevar arroba, que es el caso principal.
							 *
							 * Pero `type="email"` desactivaba implícitamente la autocapitalización y
							 * `type="text"` no, así que hay que apagarla a mano: si no, Safari iOS y
							 * Gboard mandan `Anon@example.com` y el backend busca el correo por
							 * igualdad exacta. Y `autocomplete` explícito porque al cambiar type, id
							 * y name se pierden las señales con las que los gestores de contraseñas
							 * reconocen el formulario.
							 */}
							<Input
								id="identifier"
								name="identifier"
								type="text"
								autoCapitalize="none"
								autoCorrect="off"
								spellCheck={false}
								autoComplete="username"
								value={identifier}
								onChange={(e) => {
									setIdentifier(e.target.value.trim());
									if (nifError) setNifError(null);
								}}
								onPaste={(e) => {
									e.preventDefault();
									const pastedData = e.clipboardData.getData('Text').trim();
									setIdentifier(pastedData);
									if (nifError) setNifError(null);
								}}
								placeholder="00000000T"
								className="h-11"
								required
							/>
							{nifError && (
								<p role="alert" className="text-sm text-red-500">
									{translate(
										nifError === 'correo'
											? 'general.login.emailNotAllowed'
											: 'general.login.nifInvalid'
									)}
								</p>
							)}
						</div>

						<div className="space-y-2">
							<Label htmlFor="password" className="text-sm font-medium text-gray-700">
								{translate('general.login.password')}
							</Label>
							<div className="relative">
								<Input
									id="password"
									name="password"
									type={showPassword ? 'text' : 'password'}
									autoComplete="current-password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="••••••••"
									className="h-11 pr-10"
									required
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
									aria-label={
										showPassword
											? translate('general.password.hide')
											: translate('general.password.show')
									}
								>
									{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
								</button>
							</div>
						</div>
					</CardContent>

					<CardFooter className="flex flex-col gap-4">
						<Button
							type="submit"
							className="w-full h-11 bg-arca-dark-green hover:bg-arca-dark-green/90 text-white font-medium"
							disabled={isLoading}
						>
							{isLoading ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{translate('general.login.loading') || 'Iniciando sesión...'}
								</>
							) : (
								translate('general.login.access')
							)}
						</Button>

						{biometria && (
							<Button
								type="button"
								variant="outline"
								className="w-full h-11 font-medium"
								disabled={isLoading}
								onClick={entrarConBiometria}
							>
								{biometria === 'huella' ? (
									<Fingerprint className="mr-2 h-5 w-5" />
								) : (
									<ScanFace className="mr-2 h-5 w-5" />
								)}
								{conTipo(translate, 'movil.biometria.entrar.boton', biometria)}
							</Button>
						)}

						<button
							type="button"
							onClick={() => navigate('/forget-password')}
							className="text-sm text-gray-600 hover:text-arca-dark-green transition-colors underline-offset-4 hover:underline"
						>
							{translate('general.login.forgotPassword')}
						</button>
					</CardFooter>
				</form>
			</Card>

			{/*
			 * El pie de soporte que dejó Elena. Va FUERA de la tarjeta a propósito: no es
			 * parte del formulario, es la salida para quien se atasque — y en esta pantalla
			 * quien se atasca no tiene sesión ni forma de escribir a nadie desde dentro.
			 */}
			<p className="mt-4 text-sm text-gray-600 text-center">
				{translate('general.login.contactSupportMessage')}{' '}
				<a
					href={`mailto:${ProjectConfig.ARCA_CONTACT.EMAIL}`}
					className="font-medium text-arca-dark-green underline underline-offset-2 hover:text-arca-dark-green/80"
				>
					{ProjectConfig.ARCA_CONTACT.EMAIL}
				</a>
			</p>
		</div>
	);
};

export default LoginFormV2;
